import { Component, OnInit, ViewChild } from "@angular/core";
import { MatTableDataSource } from "@angular/material/table";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { Equipment } from "../../interfaces/technical-letters/add-equipment.models";
import { TechnicalLetterService } from "../../services/technical-letters/technical-letter.service";
import { ActivatedRoute, Router } from "@angular/router";
import { LoggedUserService } from "../../services/common/logged-user.service";
import { LetterEquipment } from "../../interfaces/technical-letters/letter-equipment.models";
interface ColumnDef {
  key: string;
  label: string;
  filterable?: boolean;
}

@Component({
  selector: "app-add-equipment",
  standalone: false,
  templateUrl: "./add-equipment.component.html",
  styleUrls: ["./add-equipment.component.css"],
})
export class AddEquipmentComponent implements OnInit {
  columns: ColumnDef[] = [
    { key: "equipmentClass", label: "Equipment Class", filterable: true },
    { key: "equipmentType", label: "Equipment Type", filterable: true },
    { key: "businessUnit", label: "Business Unit", filterable: true },
    { key: "plantName", label: "Plant Name", filterable: true },
    { key: "serialNumber", label: "Serial Number", filterable: true },
    { key: "shortName", label: "Short Name", filterable: true },
    { key: "model", label: "Model", filterable: true },
    { key: "action", label: "Action" },
  ];

  oemOptions: { value: string; label: string }[] = [];
  selectedOems: string[] = [];
  displayedColumns: string[] = this.columns.map((col) => col.key);
  dataSource = new MatTableDataSource<Equipment>();
  filters: { [key: string]: string } = {};
  searchTerm: string = "";
  actionChecked: boolean = false;
  error: string | null = null;
  letterEquipmentList: Equipment[] = [];
  existingLetterEquipment: LetterEquipment[] = [];
  letterId: number = 0;
  loading = false;
  userEmail: string = "";
  showActiveOnly: boolean = false;
  showInactiveOnly: boolean = false;

  private allData: Equipment[] = [];

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private technicalLetterService: TechnicalLetterService,
    private route: ActivatedRoute,
    private Router: Router,
    private loggedUserService: LoggedUserService
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get("letterId");
    if (id) {
      this.letterId = Number(id);
    }
    this.loggedUserService.getLoggedUserData().subscribe({
      next: (data) => {
        this.userEmail = data?.email || "";
      },
    });
    this.loadOemOptions();
    this.loadExistingLetterEquipment();
    this.dataSource.sortingDataAccessor = (item, property) => {
      return (item as any)[property]
        ? (item as any)[property].toString().toLowerCase()
        : "";
    };
  }

  loadOemOptions(): void {
  this.technicalLetterService.getEquipmentLists().subscribe({
    next: (data) => {
      // Filter out empty/null OEM values and map to dropdown options
      const uniqueOems = new Set<string>();
      
      data.forEach(item => {
        if (item.oem && item.oem.trim() !== '') {
          uniqueOems.add(item.oem.trim());
        }
      });
      
      // Convert Set to array and sort alphabetically
      this.oemOptions = Array.from(uniqueOems)
        .sort()
        .map(oem => ({
          value: oem,
          label: oem
        }));
    },
    error: (error) => {
      console.error('Error loading OEM options:', error);
    }
  });
}

  loadData() {
  this.loading = true;
  this.technicalLetterService.getEquipmentLists().subscribe({
    next: (data) => {
      const filteredData = this.filterAvailableEquipment(data);
          this.allData = filteredData.map((row) => ({ ...row, selected: false }));
          this.loading = false;
    },
    error: () => {
      this.error = "Failed to load equipment data";
      this.loading = false;
    },
  });
}

  loadExistingLetterEquipment() {
    this.technicalLetterService
      .getLetterEquipmentByLetterId(this.letterId)
      .subscribe({
        next: (data: LetterEquipment[]) => {
          this.existingLetterEquipment = data;
          this.loadData();
        },
        error: (err) => {
          console.error("Failed to load existing letter equipment:", err);
          this.loadData();
        },
      });
  }

  onStatusFilterChange() {
    this.actionChecked = false;
    this.dataSource.data.forEach(row => row.selected = false);
    this.applyFilters();
    this.updateSelectAllCheckboxState();
  }

  onActionHeaderCheckboxChange(checked: boolean) {
    const visibleRecords = this.dataSource.data;

    visibleRecords.forEach(row => {
      row.selected = checked;
      const allDataItem = this.allData.find(item =>
        item.serialNumber === row.serialNumber &&
        item.equipmentClass === row.equipmentClass
      );
      if (allDataItem) {
        allDataItem.selected = checked;
      }
    });
    this.actionChecked = checked;
  }

  updateSelectAllCheckboxState() {
    const visibleRecords = this.dataSource.data;

    if (visibleRecords.length === 0) {
      this.actionChecked = false;
      return;
    }
    const selectedVisible = visibleRecords.filter(row => row.selected);
    this.actionChecked = selectedVisible.length === visibleRecords.length;
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  onAdd() {
    const letterId = this.letterId;
    const selectedEquipment = this.allData
      .filter((row) => row["selected"])
      .map((row) => ({
        equipmentType: row.equipmentClass,
        equipmentNumber: row.serialNumber,
      }));
    if (selectedEquipment.length === 0) {
      alert('Please select at least one equipment item to add.');
      return;
    }

    const payload = {
      letterId: letterId,
      equipmentList: selectedEquipment,
      userName: this.userEmail,
    };

    this.loading = true;
    this.technicalLetterService.addLetterEquipment(payload).subscribe({
      next: () => {
        this.allData = this.allData.filter(item => !item['selected']);

        this.actionChecked = false;

        this.applyFilters();

        this.loading = false;

        setTimeout(() => {
          if (window.opener) {
            window.opener.location.href = `/technical-letters/edit-technical-letters/${letterId}`;
            window.close();
          } else {
            this.Router.navigate([
              `/technical-letters/edit-technical-letters`,
              letterId,
            ]);
          }
        }, 200);
      },
      error: () => {
        this.error = "Failed to add equipment.";
        this.loading = false;
      },
    });
  }

  onFilter() {
    this.dataSource.filterPredicate = (data: Equipment, filter: string) => {
      const filters = JSON.parse(filter);
      return Object.keys(filters).every((key) => {
        const filterValue = filters[key]?.toLowerCase() || "";
        const dataValue = (data as any)[key]?.toString().toLowerCase() || "";
        return !filterValue || dataValue.includes(filterValue);
      });
    };
    this.applyFilters();
  }

  applyFilters() {
    let filtered = [...this.allData];

    if (this.showActiveOnly && !this.showInactiveOnly) {
      filtered = filtered.filter(item => !item.endServiceDate);
    } else if (!this.showActiveOnly && this.showInactiveOnly) {
      filtered = filtered.filter(item => item.endServiceDate);
    }

    // Apply multi-select OEM filter
    if (this.selectedOems && this.selectedOems.length > 0) {
      filtered = filtered.filter(item => 
        item.oem && this.selectedOems.includes(item.oem)
      );
    }

    // Apply column filters
    if (Object.keys(this.filters).some(key => this.filters[key])) {
      filtered = filtered.filter(item =>
        Object.keys(this.filters).every(key => {
          const filterValue = this.filters[key];
          if (!filterValue) return true;
          const itemValue = (item as any)[key];
          return itemValue && itemValue.toString().toLowerCase().includes(filterValue.toLowerCase());
        })
      );
    }

    // Preserve selection state
    filtered.forEach(item => {
      const match = this.allData.find(dataItem =>
        dataItem.serialNumber === item.serialNumber &&
        dataItem.equipmentClass === item.equipmentClass
      );
      if (match?.selected) {
        item.selected = true;
      }
    });
    this.dataSource.data = filtered;
  }

  onCancel() {
    this.filters = {};
    this.searchTerm = "";
    this.selectedOems = [];
    if (this.allData.length === 0) {
      this.loadExistingLetterEquipment();
    } else {
      this.dataSource.data = this.allData;
      this.applyFilters();
    }
  }

  onRowCheckboxChange(row: Equipment, checked: boolean) {
    row["selected"] = checked;

    const allDataItem = this.allData.find(item =>
      item.serialNumber === row.serialNumber &&
      item.equipmentClass === row.equipmentClass
    );
    if (allDataItem) {
      allDataItem["selected"] = checked;
    }
  }
  filterAvailableEquipment(allData: Equipment[]): Equipment[] {
    if (
      !this.existingLetterEquipment ||
      this.existingLetterEquipment.length === 0
    ) {
      return allData;
    }

    const existingSerialNumbers = this.existingLetterEquipment.map(
      (eq) => eq.equipmentNumber
    );

    return allData.filter(
      (equipment) => !existingSerialNumbers.includes(equipment.serialNumber)
    );
  }

  onOemFilterChange() {
    this.actionChecked = false;
    this.dataSource.data.forEach(row => row.selected = false);
    this.applyFilters();
    this.updateSelectAllCheckboxState();
  }
}
